import React, { useState, useRef, useCallback, useEffect } from 'react';
import PageHeader from './shared/PageHeader';
import ModelPinSelect from './shared/ModelPinSelect';
import { aiService } from '../services/aiService';
import { UploadIcon, TrashIcon, DownloadIcon, SparklesIcon, CheckIcon, XIcon, RefreshIcon } from './icons/Icons';
import AspectRatioSelector, { ORIGINAL_ASPECT_RATIO } from './shared/AspectRatioSelector';
import { useAuthStore } from '../stores/useAuthStore';
import UploadChoiceModal from './shared/UploadChoiceModal';
import GallerySelectorModal from './shared/GallerySelectorModal';
import { getImageExtFromUrl } from '../utils/imageFormat';
import { Button, IconButton, Textarea } from './ui';
import { motion, useReducedMotion, type Variants } from 'motion/react';

type ImageState = {
    file: File | string;
    url: string;
    backendId?: number;
    selected?: boolean;
};

// Page-load entrance: base=300ms, slow=500ms — numeric equivalents of
// --snapfy-duration-base and --snapfy-duration-slow; easing mirrors --snapfy-ease-standard.
const pageVariants: Variants = {
    hidden: { opacity: 0, y: 16 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] as const } },
} as const;

const Editor: React.FC = () => {
    const maxResolution = useAuthStore(state => state.user?.maxResolution);
    const [mainImage, setMainImage] = useState<ImageState | null>(null);
    const [editedImageUrl, setEditedImageUrl] = useState<string | null>(null);
    const [editedImageId, setEditedImageId] = useState<number | null>(null);
    const [referenceImages, setReferenceImages] = useState<ImageState[]>([]);
    const [prompt, setPrompt] = useState<string>('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [isDragging, setIsDragging] = useState(false);
    const [isRefDragging, setIsRefDragging] = useState(false);
    const [history, setHistory] = useState<ImageState[]>([]);
    // Default 'Original': uma edicao nao deve reenquadrar a foto. Forcar 1:1
    // sobre uma foto 3:2 obrigava o modelo a inventar as bordas que faltavam.
    const [aspectRatio, setAspectRatio] = useState(ORIGINAL_ASPECT_RATIO);
    const [resolution, setResolution] = useState<'1K' | '2K' | '4K'>('1K');
    // Modelo fixado para esta edição (só equipe interna; undefined = cadeia padrão).
    const [pinnedModel, setPinnedModel] = useState<string | undefined>(undefined);
    const [outputFormat, setOutputFormat] = useState<'png' | 'jpeg'>('png');
    const [isDownloading, setIsDownloading] = useState(false);

    const [isUploadChoiceOpen, setIsUploadChoiceOpen] = useState(false);
    const [isGalleryOpen, setIsGalleryOpen] = useState(false);

    const prefersReducedMotion = useReducedMotion();

    const mainImageInputRef = useRef<HTMLInputElement>(null);

    const handleDownload = async (src: string, filename: string, backendId?: number) => {
        if (isDownloading) return;
        setIsDownloading(true);
        try {
            // Preferred path: use the backend proxy endpoint (already handles
            // Content-Disposition: attachment) instead of fetching the S3 URL
            // directly. The direct fetch fails on CORS and we end up falling
            // back to window.open, which opens the image in a new tab.
            const blob = backendId
                ? await aiService.downloadImageBlob(backendId)
                : await (await fetch(src)).blob();

            const blobUrl = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = blobUrl;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(blobUrl);
        } catch (error) {
            console.error('Download failed:', error);
            window.open(src, '_blank');
        } finally {
            setIsDownloading(false);
        }
    };

    const handleMainImageUpload = (img: ImageState) => {
        setMainImage(img);
        setEditedImageUrl(null);
        setHistory([]); // Reset history on new upload
    };

    // Clear the image being edited so the user can load another one without
    // having to refresh the page. Resets every piece of edit state.
    const handleClearMainImage = () => {
        setMainImage(null);
        setEditedImageUrl(null);
        setEditedImageId(null);
        setReferenceImages([]);
        setHistory([]);
        setPrompt('');
        setError(null);
    };

    const handleMainImageFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            handleMainImageUpload({ file, url: URL.createObjectURL(file) });
        }
    };

    const handleAddReferenceImage = (file: File) => {
        if (file && file.type.startsWith('image/') && referenceImages.length < 3) {
            setReferenceImages(prev => [...prev, {
                file,
                url: URL.createObjectURL(file),
                selected: true // ✅ Auto-select new reference images
            }]);
        }
    };

    const handleReferenceImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            handleAddReferenceImage(file);
        }
    };

    const handleUploadAreaClick = () => {
        setIsUploadChoiceOpen(true);
    };

    const handleGalleryImageSelect = (url: string, image?: import('./shared/GallerySelectorModal').GallerySelectedImage) => {
        handleMainImageUpload({
            file: url,
            url,
            backendId: image?.backendId ? Number(image.backendId) : undefined,
        });
        setIsGalleryOpen(false);
    };

    const handleDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(true);
    };

    const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
    };

    const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
        const file = e.dataTransfer.files?.[0];
        if (file && file.type.startsWith('image/')) {
            handleMainImageUpload({ file, url: URL.createObjectURL(file) });
        }
        if (e.dataTransfer) {
            e.dataTransfer.clearData();
        }
    };

    const handleRefDragEnter = (e: React.DragEvent<HTMLLabelElement>) => {
        e.preventDefault();
        e.stopPropagation();
        setIsRefDragging(true);
    };
    const handleRefDragLeave = (e: React.DragEvent<HTMLLabelElement>) => {
        e.preventDefault();
        e.stopPropagation();
        setIsRefDragging(false);
    };
    const handleRefDragOver = (e: React.DragEvent<HTMLLabelElement>) => {
        e.preventDefault();
        e.stopPropagation();
    };
    const handleRefDrop = (e: React.DragEvent<HTMLLabelElement>) => {
        e.preventDefault();
        e.stopPropagation();
        setIsRefDragging(false);
        const file = e.dataTransfer.files?.[0];
        if (file) {
            handleAddReferenceImage(file);
        }
    };

    const handleGenerate = async () => {
        if (!mainImage) {
            setError('Por favor, envie uma imagem para editar.');
            return;
        }
        if (!prompt.trim()) {
            setError('Por favor, descreva a edição desejada.');
            return;
        }

        setIsLoading(true);
        setError(null);
        try {

            // ✅ Filter only SELECTED reference images
            const selectedRefs = referenceImages.filter(img => img.selected);

            // ✅ Use backendId if available, otherwise use File object
            const refFiles = selectedRefs.map(img =>
                img.backendId ? img.backendId : (img.file instanceof File ? img.file : undefined)
            ).filter((f): f is File | number => f !== undefined);

            // Use backendId if available, otherwise convert URL/File to a File object for upload
            let imageToEdit: File | number;
            if (mainImage.backendId) {
                imageToEdit = mainImage.backendId;
            } else if (mainImage.file instanceof File) {
                imageToEdit = mainImage.file;
            } else if (typeof mainImage.file === 'string' || typeof mainImage.url === 'string') {
                // URL string (e.g., from gallery without backendId) — convert to File
                const imgUrl = typeof mainImage.file === 'string' ? mainImage.file : mainImage.url;
                const response = await fetch(imgUrl);
                const blob = await response.blob();
                imageToEdit = new File([blob], 'edit-source.jpg', { type: blob.type || 'image/jpeg' });
            } else {
                setError('Imagem inválida. Tente fazer upload novamente.');
                return;
            }

            const result = await aiService.editImage(imageToEdit, prompt, undefined, refFiles, aspectRatio, resolution, undefined, outputFormat, pinnedModel);

            if (result && result.url) {
                setEditedImageUrl(result.url);
                setEditedImageId(result.id); // ✅ Store backend ID
            } else {
                setError('A API não retornou uma imagem. Tente novamente.');
            }
        } catch (e: any) {
            console.error("Edit failed:", e);
            setError(e.message || 'Ocorreu um erro durante a edição.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleKeep = () => {
        if (editedImageUrl && mainImage && editedImageId) {
            setHistory(prev => [...prev, mainImage]);
            setMainImage({
                file: editedImageUrl,
                url: editedImageUrl,
                backendId: editedImageId // ✅ Store backend ID for next edit
            });
            setEditedImageUrl(null);
            setEditedImageId(null);
            setPrompt('');

            // ✅ Unselect all reference images after successful edit
            setReferenceImages(prev => prev.map(img => ({ ...img, selected: false })));
        }
    };

    const handleDiscard = () => {
        setEditedImageUrl(null);
    };

    const handleUndo = () => {
        if (history.length > 0) {
            const previous = history[history.length - 1];
            setMainImage(previous);
            setHistory(prev => prev.slice(0, -1));
            setEditedImageUrl(null);
        }
    };

    return (

        <motion.div
            className="space-y-8 h-[calc(100vh-220px)] lg:h-[calc(100vh-100px)] flex flex-col"
            variants={prefersReducedMotion ? undefined : pageVariants}
            initial={prefersReducedMotion ? undefined : 'hidden'}
            animate={prefersReducedMotion ? undefined : 'visible'}
        >
            <PageHeader
                title="Editor"
                subtitle="Edite áreas específicas da sua imagem com a ajuda da IA."
                infoTooltip="Edite imagens com IA."
                icon={
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2} className="w-full h-full">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.5L15.232 5.232z" />
                  </svg>
                }
            />

            <div className="flex-1 flex flex-col lg:flex-row gap-6 p-6 bg-snapfy-surface rounded-xl min-h-0">
                {/* Controls Panel */}
                <div className="lg:w-1/4 flex flex-col gap-6 overflow-y-auto min-h-0 max-h-full">
                    <div>
                        <div className="flex items-center justify-between mb-2">
                            <label htmlFor="edit-prompt" className="block text-sm font-bold text-snapfy-text-secondary">Prompt de Edição</label>
                            {/* "Melhorar" — ghost Button; no ref needed, inline onClick preserved */}
                            <Button
                                variant="ghost"
                                onClick={async () => {
                                    if (!prompt || !prompt.trim() || !mainImage) return;
                                    setIsLoading(true);
                                    try {
                                        const refFiles = referenceImages.map(img => img.file instanceof File ? img.file : undefined).filter((f): f is File => f !== undefined);
                                        const optimized = await aiService.optimizePrompt(prompt, mainImage.url, refFiles);
                                        setPrompt(optimized || prompt);
                                    } catch (e) {
                                        console.error("Failed to optimize", e);
                                    } finally {
                                        setIsLoading(false);
                                    }
                                }}
                                disabled={isLoading || !prompt || !prompt.trim() || !mainImage}
                                className="text-xs text-snapfy-accent-soft hover:text-snapfy-accent-soft gap-1 px-2 py-1"
                                title="Usar IA para melhorar o prompt"
                            >
                                <SparklesIcon size={14} />
                                Melhorar
                            </Button>
                        </div>
                        {/* Textarea primitive — id passed to match existing <label htmlFor="edit-prompt"> */}
                        <Textarea
                            id="edit-prompt"
                            value={prompt}
                            onChange={(e) => setPrompt(e.target.value)}
                            placeholder="Ex: Mude a cor da camisa para azul..."
                            className="min-h-[100px] resize-y"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-bold text-snapfy-text-secondary mb-2">Imagens de Referência (Opcional)</label>
                        <div className="grid grid-cols-3 gap-2">
                            {referenceImages.map((img, index) => (
                                <div
                                    key={index}
                                    className={`relative group aspect-square cursor-pointer border-2 rounded-xl transition-all ${img.selected ? 'border-snapfy-primary shadow-[0_0_10px_rgba(168,85,247,0.3)]' : 'border-transparent hover:border-snapfy-border'}`}
                                    onClick={() => {
                                        // ✅ Toggle selection
                                        setReferenceImages(prev => prev.map((item, i) =>
                                            i === index ? { ...item, selected: !item.selected } : item
                                        ));
                                    }}
                                >
                                    <img src={img.url} alt={`Referência ${index + 1}`} className="w-full h-full object-cover rounded-md" />

                                    {/* ✅ Selection Indicator */}
                                    {img.selected && (
                                        <div className="absolute top-1 left-1 bg-snapfy-primary text-white rounded-full p-1 shadow-md z-10">
                                            <CheckIcon size={12} />
                                        </div>
                                    )}

                                    {/* Delete button — IconButton (icon-only); onClick + stopPropagation preserved */}
                                    <IconButton
                                        aria-label="Remover imagem de referência"
                                        onClick={(e) => {
                                            e.stopPropagation(); // Prevent toggle when clicking delete
                                            setReferenceImages(prev => prev.filter((_, i) => i !== index));
                                        }}
                                        className="absolute top-1 right-1 w-6 h-6 bg-black/50 opacity-0 group-hover:opacity-100 hover:bg-snapfy-error z-10"
                                    >
                                        <TrashIcon className="w-3 h-3" />
                                    </IconButton>
                                </div>
                            ))}
                            {referenceImages.length < 3 && (
                                <label
                                    onDrop={handleRefDrop}
                                    onDragOver={handleRefDragOver}
                                    onDragEnter={handleRefDragEnter}
                                    onDragLeave={handleRefDragLeave}
                                    className={`w-full aspect-square flex flex-col items-center justify-center text-snapfy-text-secondary border-2 border-dashed rounded-md cursor-pointer transition-colors ${isRefDragging ? 'bg-snapfy-inner border-snapfy-primary' : 'border-snapfy-border hover:bg-snapfy-inner hover:border-snapfy-primary'
                                        }`}>
                                    <UploadIcon />
                                    <span className="text-xs mt-1">{isRefDragging ? 'Soltar' : 'Adicionar'}</span>
                                    <input id="ref-image-upload" type="file" className="hidden" accept="image/*" onChange={handleReferenceImageUpload} />
                                </label>
                            )}
                        </div>
                    </div>

                    <div>
                        <h3 className="text-sm font-medium text-snapfy-text-secondary mb-2">Proporção de Saída</h3>
                        <AspectRatioSelector
                            aspectRatio={aspectRatio}
                            onAspectRatioChange={setAspectRatio}
                            resolution={resolution}
                            onResolutionChange={(r) => setResolution(r as '1K' | '2K' | '4K')}
                            maxResolution={maxResolution}
                            outputFormat={outputFormat}
                            onOutputFormatChange={setOutputFormat}
                            modalPosition="bottom"
                        />
                    </div>

                    <ModelPinSelect value={pinnedModel} onChange={setPinnedModel} disabled={isLoading} />

                    <div className="mt-auto space-y-3">
                        {error && <div className="text-center text-sm text-snapfy-error bg-snapfy-error/10 p-2 rounded-xl">{error}</div>}

                        {/* History / Undo Control */}
                        {history.length > 0 && !editedImageUrl && (
                            <Button
                                variant="ghost"
                                onClick={handleUndo}
                                disabled={isLoading}
                                className="w-full"
                                leadingIcon={<RefreshIcon className="w-4 h-4" />}
                            >
                                Desfazer Última Edição
                            </Button>
                        )}

                        {/* Generate CTA — cta gradient (mock title-case recipe) */}
                        <Button
                            variant="cta"
                            onClick={handleGenerate}
                            disabled={isLoading || !mainImage || !prompt.trim() || !!editedImageUrl}
                            isLoading={isLoading}
                            className="w-full"
                        >
                            {isLoading ? 'Gerando...' : 'Gerar (1 crédito)'}
                        </Button>
                    </div>
                </div>

                {/* Image Viewer Area */}
                <div className="flex-1 lg:w-3/4 flex flex-col gap-4 bg-snapfy-inner/50 rounded-xl p-4 min-h-0 relative">
                    {!mainImage ? (
                        <div
                            onClick={handleUploadAreaClick}
                            onDrop={handleDrop} onDragOver={handleDragOver} onDragEnter={handleDragEnter} onDragLeave={handleDragLeave}
                            className={`w-full h-full rounded-xl border-2 border-dashed flex flex-col items-center justify-center text-snapfy-text-secondary cursor-pointer transition-colors ${isDragging ? 'bg-snapfy-inner border-snapfy-primary' : 'bg-snapfy-inner border-snapfy-border hover:bg-snapfy-inner hover:border-snapfy-border-strong'
                                }`}
                        >
                            {/* File input kept native — ref={mainImageInputRef} required for mainImageInputRef.current?.click() */}
                            <input ref={mainImageInputRef} type="file" className="hidden" accept="image/*" onChange={handleMainImageFileChange} />
                            <UploadIcon />
                            <span className="mt-2 text-sm">Envie uma imagem para começar</span>
                        </div>
                    ) : (
                        <>
                            {/* View Area */}
                            <div className="flex-1 w-full min-h-0 flex gap-4">
                                {/* If editedImageUrl exists, show Comparison View */}
                                {editedImageUrl ? (
                                    <>
                                        {/* Original (Left) */}
                                        <div className="flex-1 flex flex-col gap-2 min-w-0">
                                            <span className="text-sm font-medium text-snapfy-text-secondary text-center uppercase tracking-wider">Original</span>
                                            <div className="flex-1 relative rounded-xl overflow-hidden border border-snapfy-border bg-black/20">
                                                <img src={mainImage.url} alt="Original" className="absolute inset-0 w-full h-full object-contain" />
                                            </div>
                                        </div>

                                        {/* Result (Right) */}
                                        <div className="flex-1 flex flex-col gap-2 min-w-0">
                                            <span className="text-sm font-medium text-snapfy-accent-soft text-center uppercase tracking-wider">Resultado</span>
                                            {/* shadow: rgba(168,85,247,...) — sanctioned residual (gradient stop family, Phase 7) */}
                                            <div className="flex-1 relative rounded-xl overflow-hidden border border-snapfy-primary/30 bg-black/20 shadow-[0_0_15px_rgba(168,85,247,0.15)]">
                                                <img src={editedImageUrl} alt="Resultado" className="absolute inset-0 w-full h-full object-contain" />
                                            </div>
                                        </div>
                                    </>
                                ) : (
                                    /* Single View (Main Image) */
                                    <div className="flex-1 relative rounded-xl overflow-hidden border border-snapfy-border bg-black/20">
                                        <img src={mainImage.url} alt="Original" className="absolute inset-0 w-full h-full object-contain" />

                                        {/* Loading Overlay */}
                                        {isLoading && (
                                            <div className="absolute inset-0 bg-black/80 flex flex-col items-center justify-center text-white rounded-xl">
                                                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-snapfy-accent-soft"></div>
                                                <p className="mt-4 text-lg font-semibold">Aguardando processamento da edição...</p>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>

                            {/* Action Bar (Only visible when reviewing an edit) */}
                            {editedImageUrl && (
                                <div className="flex items-center justify-center gap-4 p-4 bg-snapfy-inner/80 backdrop-blur-sm rounded-xl border border-snapfy-border shadow-card absolute bottom-8 left-1/2 transform -translate-x-1/2 z-10">
                                    {/* Discard — kept native: no DS primitive for destructive red variant */}
                                    <button
                                        onClick={handleDiscard}
                                        className="flex items-center gap-2 px-6 py-2.5 text-snapfy-error hover:text-red-300 hover:bg-snapfy-error/10 rounded-xl transition-all font-medium"
                                    >
                                        <XIcon className="w-5 h-5" />
                                        Descartar
                                    </button>
                                    <div className="w-px h-8 bg-snapfy-inner mx-2"></div>
                                    {/* Keep & Continue — solid Button; onClick preserved */}
                                    <Button
                                        variant="solid"
                                        onClick={handleKeep}
                                        leadingIcon={<CheckIcon className="w-5 h-5" />}
                                        className="rounded-xl"
                                    >
                                        Manter e Continuar
                                    </Button>
                                </div>
                            )}

                            {/* Action buttons (Always available for current view) */}
                            {!editedImageUrl && (
                                <div className="absolute top-4 right-4 flex items-center gap-2">
                                    {/* Remove current image so another can be edited without a page refresh */}
                                    <IconButton
                                        aria-label="Remover imagem"
                                        onImagery
                                        onClick={handleClearMainImage}
                                        disabled={isLoading}
                                    >
                                        <TrashIcon className="w-5 h-5" />
                                    </IconButton>
                                    {/* Download — IconButton onImagery; onClick preserved; isDownloading state drives content */}
                                    <IconButton
                                        aria-label={isDownloading ? 'Aguarde o download...' : 'Baixar Imagem Atual'}
                                        onImagery
                                        onClick={() => handleDownload(mainImage.url, `snapfy-image-${Date.now()}.${getImageExtFromUrl(mainImage.url)}`, mainImage.backendId)}
                                        disabled={isDownloading}
                                    >
                                        {isDownloading ? (
                                            <div className="w-5 h-5 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                                        ) : (
                                            <DownloadIcon className="w-5 h-5" />
                                        )}
                                    </IconButton>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>

            <UploadChoiceModal
                isOpen={isUploadChoiceOpen}
                onClose={() => setIsUploadChoiceOpen(false)}
                onChooseFile={() => {
                    mainImageInputRef.current?.click();
                }}
                onChooseGallery={() => {
                    setIsUploadChoiceOpen(false);
                    setIsGalleryOpen(true);
                }}
            />

            <GallerySelectorModal
                isOpen={isGalleryOpen}
                onClose={() => setIsGalleryOpen(false)}
                onSelectImage={handleGalleryImageSelect}
            />
        </motion.div>
    );
};

export default Editor;
